'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const TICK_MS = 50;
function randomChar() {
  return CHARSET[Math.floor(Math.random() * CHARSET.length)];
}

/**
 * Get word boundaries: [{ start, end }, ...] for each word in text.
 */
function getWordBounds(text) {
  const words = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === ' ') {
      i++;
      continue;
    }
    const start = i;
    while (i < text.length && text[i] !== ' ') i++;
    words.push({
      start,
      end: i
    });
  }
  return words;
}

/**
 * Group words into batches of 1-3 for settling.
 */
function batchWords(words) {
  const batches = [];
  let i = 0;
  while (i < words.length) {
    const size = Math.min(1 + Math.floor(Math.random() * 3), words.length - i);
    batches.push(words.slice(i, i + size));
    i += size;
  }
  return batches;
}
function settled(text, maxLen) {
  const len = maxLen ?? text.length;
  return Array.from({
    length: len
  }, (_, i) => ({
    display: i < text.length ? text[i] : '',
    style: 'normal'
  }));
}
function settledAlt(text, altText, maxLen) {
  const alt = altText || text;
  const len = maxLen ?? Math.max(text.length, alt.length);
  return Array.from({
    length: len
  }, (_, i) => ({
    display: i < alt.length ? alt[i] : '',
    style: 'alt'
  }));
}

/**
 * GlitchText — renders text with synchronized glitch effects.
 *
 * Phase is controlled externally by useGlitchCycle hook (shared clock).
 * This component only handles per-character rendering and word-by-word settling.
 *
 * Props:
 *   text         — base text (the "truth")
 *   altText      — alternate text (propaganda) — optional
 *   className    — base color/styling
 *   altClassName — color for altText chars (defaults to className)
 *   phase        — current cycle phase from useGlitchCycle
 *   takeoverMs   — duration of takeover phase (for word settle timing)
 *   restoreMs    — duration of restore phase (for word settle timing)
 */
export default function GlitchText(t0) {
  const $ = _c(27);
  const {
    text,
    altText,
    className,
    altClassName,
    phase: t1,
    takeoverMs: t2,
    restoreMs: t3
  } = t0;
  const phase = t1 === undefined ? "idle" : t1;
  const takeoverMs = t2 === undefined ? 800 : t2;
  const restoreMs = t3 === undefined ? 800 : t3;
  let t4;
  if ($[0] !== text) {
    t4 = () => settled(text);
    $[0] = text;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const [chars, setChars] = useState(t4);
  const intervalRef = useRef(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const timersRef = useRef(t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = null;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const clearTimers = t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (fn, ms) => {
      const id = setTimeout(fn, ms);
      timersRef.current.push(id);
      return id;
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const addTimeout = t7;
  let t8;
  let t9;
  if ($[5] !== altText || $[6] !== phase || $[7] !== restoreMs || $[8] !== takeoverMs || $[9] !== text) {
    t8 = () => {
      clearTimers();
      const alt = altText || text;
      const maxLen = Math.max(text.length, alt.length);
      const altWords = getWordBounds(alt);
      const altBatches = batchWords(altWords);
      const textWords = getWordBounds(text);
      const textBatches = batchWords(textWords);
      bb25: switch (phase) {
        case "idle":
          {
            setChars(settled(text, maxLen));
            break bb25;
          }
        case "takeover":
          {
            intervalRef.current = setInterval(() => {
              setChars(_temp2);
            }, TICK_MS);
            const perBatch = altBatches.length > 0 ? takeoverMs / altBatches.length : takeoverMs;
            altBatches.forEach((batch_0, bi_0) => {
              addTimeout(() => {
                setChars(prev_2 => {
                  const padded = prev_2.length < maxLen ? [...prev_2, ...Array.from({
                    length: maxLen - prev_2.length
                  }, _temp3)] : prev_2;
                  return padded.map((c_1, idx_0) => {
                    for (const w_0 of batch_0) {
                      if (idx_0 >= w_0.start && idx_0 < w_0.end) {
                        return {
                          display: idx_0 < alt.length ? alt[idx_0] : "",
                          style: "alt"
                        };
                      }
                    }
                    return c_1;
                  });
                });
              }, bi_0 * perBatch);
            });
            break bb25;
          }
        case "hold":
          {
            setChars(settledAlt(text, altText, maxLen));
            break bb25;
          }
        case "fight":
          {
            intervalRef.current = setInterval(() => {
              setChars(Array.from({
                length: maxLen
              }, (__0, i) => {
                const tc = i < text.length ? text[i] : "";
                const ac = i < alt.length ? alt[i] : "";
                if (tc === " " && ac === " ") {
                  return {
                    display: " ",
                    style: "normal"
                  };
                }
                if (!tc && !ac) {
                  return {
                    display: "",
                    style: "normal"
                  };
                }
                const roll = Math.random();
                if (roll < 0.25) {
                  return {
                    display: tc || ac,
                    style: "normal"
                  };
                }
                if (roll < 0.5) {
                  return {
                    display: ac || tc,
                    style: "alt"
                  };
                }
                if (roll < 0.8) {
                  return {
                    display: randomChar(),
                    style: "cyberstan"
                  };
                }
                return {
                  display: randomChar(),
                  style: "normal"
                };
              }));
            }, TICK_MS);
            break bb25;
          }
        case "restore":
          {
            intervalRef.current = setInterval(() => {
              setChars(_temp5);
            }, TICK_MS);
            const perBatchR = textBatches.length > 0 ? restoreMs / textBatches.length : restoreMs;
            textBatches.forEach((batch, bi) => {
              addTimeout(() => {
                setChars(prev_0 => {
                  const result = Array.from({
                    length: maxLen
                  }, (_, idx) => {
                    for (const w of batch) {
                      if (idx >= w.start && idx < w.end) {
                        return {
                          display: idx < text.length ? text[idx] : "",
                          style: "normal"
                        };
                      }
                    }
                    return idx < prev_0.length ? prev_0[idx] : {
                      display: "",
                      style: "normal"
                    };
                  });
                  return result;
                });
              }, bi * perBatchR);
            });
          }
      }
      return clearTimers;
    };
    t9 = [phase, text, altText, takeoverMs, restoreMs, clearTimers, addTimeout];
    $[5] = altText;
    $[6] = phase;
    $[7] = restoreMs;
    $[8] = takeoverMs;
    $[9] = text;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  const allNormal = chars.every(_temp6);
  if (allNormal) {
    let t10;
    if ($[12] !== chars) {
      t10 = chars.map(_temp7).join("");
      $[12] = chars;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== className || $[15] !== t10) {
      t11 = <span className={className}>{t10}</span>;
      $[14] = className;
      $[15] = t10;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    return t11;
  }
  let t10;
  if ($[17] !== altClassName || $[18] !== chars || $[19] !== className) {
    let t11;
    if ($[21] !== altClassName || $[22] !== className) {
      t11 = (c_4, i_0) => {
        if (c_4.style === "cyberstan") {
          return <span key={i_0} className="glitch-char">{c_4.display}</span>;
        }
        if (c_4.style === "alt" && altClassName && altClassName !== className) {
          return <span key={i_0} className={altClassName}>{c_4.display}</span>;
        }
        return c_4.display;
      };
      $[21] = altClassName;
      $[22] = className;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t10 = chars.map(t11);
    $[17] = altClassName;
    $[18] = chars;
    $[19] = className;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[24] !== className || $[25] !== t10) {
    t11 = <span className={className}>{t10}</span>;
    $[24] = className;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function _temp7(c_3) {
  return c_3.display;
}
function _temp6(c_2) {
  return c_2.style === "normal";
}
function _temp5(prev) {
  return prev.map(_temp4);
}
function _temp4(c) {
  return c.style !== "normal" && c.display !== "" ? {
    display: randomChar(),
    style: "cyberstan"
  } : c;
}
function _temp3() {
  return {
    display: "",
    style: "normal"
  };
}
function _temp2(prev_1) {
  return prev_1.map(_temp);
}
function _temp(c_0) {
  return c_0.style === "normal" && c_0.display !== " " && c_0.display !== "" ? {
    display: randomChar(),
    style: "cyberstan"
  } : c_0;
}